import history from '../history';

// How long the ac_* hint fetch may delay login before it is abandoned. Deliberately short: the
// hint layer is a courtesy and the server remains authoritative, so a slow one must cost a
// slightly less helpful UI, never a login. See loadPermissions().
const AC_FETCH_TIMEOUT_MS = 3000;

export default class Auth {
  getAccessToken() {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      return null; // Return null instead of throwing error for session manager
    }
    return accessToken;
  }


  getContext() {
    const yourContext = localStorage.getItem('context');
    if (!yourContext) {
      return null;
    }
    return JSON.parse(yourContext);
  }

  setContextSession(context) {
    localStorage.setItem('context', JSON.stringify(context));
  }

  getOrgId() {
    const orgId = localStorage.getItem('orgId');
    if (!orgId) {
      throw new Error('No org id found');
    }
    return orgId;
  }

  getRoleId() {
    const roleId = localStorage.getItem('roleId');
    if (!roleId) {
      return Number(0);
    }
    return Number(roleId);
  }

  getUserId() {
    try {
      const context = this.getContext();
      return context?.userId || context?.id || null;
    } catch (error) {
      return null;
    }
  }

  getUserEmail() {
    const userEmail = localStorage.getItem('userEmail');

    // Check if userEmail exists and looks like an email
    if (userEmail && userEmail.includes('@')) {
      return userEmail;
    }

    return null;
  }


  async logout() {
    // Revoke the session server-side before clearing local state
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        await fetch('/auth/logout', {
          method: 'POST',
          headers: {'x-access-token': token},
        });
      }
    } catch (_) {
      // Proceed with local cleanup even if the server call fails
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('saganUsername');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('orgId');
    localStorage.removeItem('roleId');
    localStorage.removeItem('context');
    localStorage.removeItem('userPermissions');
    localStorage.removeItem('userRoles');
    localStorage.removeItem('isSuperUser');
    localStorage.removeItem('acPermissions');
    localStorage.removeItem('acEnforcement');
    localStorage.removeItem('acSubjectResolved');
    history.replace('/login');
  }

  async setSession(user) {
    // Handle both login response and token refresh response
    let expiresAt;
    let token;

    if (user.accessToken) {
      // Token refresh response
      token = user.accessToken;
      expiresAt = JSON.stringify((user.expiresIn * 1000) + new Date().getTime());
      localStorage.setItem('access_token', token);
      localStorage.setItem('expires_at', expiresAt);
    } else {
      // Login response
      token = user.token;
      expiresAt = JSON.stringify((user.expireIn * 1000) + new Date().getTime());
      localStorage.setItem('access_token', token);
      localStorage.setItem('expires_at', expiresAt);
      localStorage.setItem('saganUsername', user.username);
      localStorage.setItem('userEmail', user.email);
      localStorage.setItem('orgId', user.org);
      localStorage.setItem('roleId', user.role);
      localStorage.setItem('context', JSON.stringify(user.context));
    }
    await this.loadPermissions();
  }

  // Two independently-caught fetches under Promise.allSettled, NOT a sequential await
  // (docs/access/slice-4b-admin-surface-plan.md §3.3) — the incumbent call below used to `return`
  // early on `!response.ok`, which would silently skip a second fetch written after it, and
  // `setSession` awaits this whole method on the login path, so a serial second call would add a
  // full round trip to every login. allSettled gives isolation and parallelism together: neither
  // fetch's failure can affect the other, and a hint-layer outage cannot break login.
  async loadPermissions() {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'x-access-token': token,
    };

    // THE AC FETCH GETS A DEADLINE, and allSettled alone is not enough.
    //
    // allSettled isolates REJECTION, not LATENCY: it resolves only once every input has settled.
    // setSession awaits this method and login.actions.js awaits setSession before dispatching
    // LOGIN_SUCCESS — so a /access/permissions/me that never answers hangs the login screen for
    // EVERY user, including ones who will never open the admin page. The plan's §3.3 claimed
    // "neither can fail the other"; that was true of failure and false of silence. `fetch` has no
    // default timeout, and the realistic trigger is mundane: the pool saturated by a dictionary
    // import while the two subject queries queue behind it.
    //
    // An aborted fetch rejects, so it lands in the existing `rejected` branch and the hint layer
    // simply keeps whatever it had — the documented over-show direction.
    const acAbort = new AbortController();
    const acTimer = setTimeout(() => acAbort.abort(), AC_FETCH_TIMEOUT_MS);

    const [incumbent, ac] = await Promise.allSettled([
      fetch('/userProfile/permissions/me', {method: 'GET', headers}),
      fetch('/access/permissions/me', {method: 'GET', headers, signal: acAbort.signal}),
    ]);
    clearTimeout(acTimer);

    try {
      if (incumbent.status === 'fulfilled' && incumbent.value.ok) {
        const data = await incumbent.value.json();
        const {permissions, roles, isSuperUser} = data.response || {};
        localStorage.setItem('userPermissions', JSON.stringify(permissions || []));
        localStorage.setItem('userRoles', JSON.stringify(roles || []));
        localStorage.setItem('isSuperUser', JSON.stringify(isSuperUser || false));
      }
    } catch (err) {
      console.error('loadPermissions error:', err);
    }

    try {
      if (ac.status === 'fulfilled' && ac.value.ok) {
        const data = await ac.value.json();
        localStorage.setItem('acPermissions', JSON.stringify(data.permissions || []));
        localStorage.setItem('acEnforcement', JSON.stringify(data.enforcement || {mode: 'none', modules: []}));
        // §3.7 — the endpoint answers 200 with an empty list when the caller has no ACTIVE access
        // subject, so an empty `acPermissions` alone cannot distinguish "holds nothing" from
        // "unresolvable". Only this flag can, and an admin in that state will be refused
        // NO_ACTOR_SUBJECT by every 4a write path — so the access-admin page surfaces it rather
        // than letting them discover it on a failed grant.
        localStorage.setItem('acSubjectResolved', JSON.stringify(data.subjectResolved !== false));
        localStorage.setItem('acExpiringAuthority', JSON.stringify(data.expiringAuthority || null));
      }
    } catch (err) {
      console.error('loadPermissions (ac) error:', err);
    }
  }

  /**
   * The expiry banner's payload (slice 6a-2 §3.5), or null.
   *
   * THE PAST-DATE GUARD IS NOT DEFENSIVE PADDING. This block only overwrites when `ac.value.ok`,
   * so a timed-out or 500 refresh leaves the last good payload in place — and after the expiry
   * date that payload renders a banner saying access "ends" on a day already gone. The user
   * decided there is NO post-expiry message; without this guard a failed fetch reintroduces one.
   */
  getAcExpiringAuthority() {
    try {
      const raw = localStorage.getItem('acExpiringAuthority');
      const val = raw ? JSON.parse(raw) : null;
      if (!val || !val.date) return null;
      return new Date(val.date).getTime() > Date.now() ? val : null;
    } catch (e) {
      return null;
    }
  }

  getAcPermissions() {
    try {
      const perms = localStorage.getItem('acPermissions');
      return perms ? JSON.parse(perms) : [];
    } catch (e) {
      return [];
    }
  }

  getAcEnforcement() {
    try {
      const val = localStorage.getItem('acEnforcement');
      return val ? JSON.parse(val) : {mode: 'none', modules: []};
    } catch (e) {
      return {mode: 'none', modules: []};
    }
  }

  /**
   * Whether the caller resolved to an ACTIVE access subject (§3.7).
   *
   * Defaults TRUE when the key is absent, which is the whole point: a session predating this
   * deploy, or one whose hint fetch failed, has no flag — and warning those users that their
   * access is broken, when nothing is known either way, would be a false alarm on every login.
   * Only an explicit `false` from the endpoint raises it.
   */
  getAcSubjectResolved() {
    try {
      const val = localStorage.getItem('acSubjectResolved');
      return val === null ? true : JSON.parse(val);
    } catch (e) {
      return true;
    }
  }

  hasPermission(featureName, permissionType = 'can_view') {
    if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_RBAC_FALLBACK_MODE === 'true') {
      return true;
    }
    if (this.getIsSuperUser()) return true;
    const permissions = this.getPermissions();
    if (!permissions || permissions.length === 0) {
      return false;
    }
    const perm = permissions.find((p) => p.feature_name === featureName);
    // eslint-disable-next-line security/detect-object-injection
    return perm ? Boolean(perm[permissionType]) : false;
  }

  getPermissions() {
    try {
      const perms = localStorage.getItem('userPermissions');
      return perms ? JSON.parse(perms) : [];
    } catch (e) {
      return [];
    }
  }

  getIsSuperUser() {
    try {
      const val = localStorage.getItem('isSuperUser');
      return val ? JSON.parse(val) : false;
    } catch (e) {
      return false;
    }
  }

  getUserRoles() {
    try {
      const roles = localStorage.getItem('userRoles');
      return roles ? JSON.parse(roles) : [];
    } catch (e) {
      return [];
    }
  }

  isAuthenticated() {
    // Check whether the current time is past the
    // access token's expiry time
    const expiresAt = JSON.parse(localStorage.getItem('expires_at'));
    return new Date().getTime() < expiresAt;
  }
}
